import React, { useState } from 'react';
import { Documento, TipoDocumento } from '@/types/presupuesto';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { FileText, Download, Trash2, Edit2, Plus, Eye, MessageCircle } from 'lucide-react';
import { DocumentoDialog } from './DocumentoDialog';
import { usePDFViewer } from '@/contexts/PDFViewerContext';
import { presupuestoService } from '@/services/presupuestoService';
import WhatsAppContabilidadDocumentoModal from '@/components/WhatsAppContabilidadDocumentoModal';

interface SeccionDocumentacionProps {
  tramiteId: string;
  tramite: any;
  documentos: Documento[];
  onCargarDocumento: (
    tipo: TipoDocumento,
    nombreCustom: string,
    file: File
  ) => Promise<any>;
  onActualizarDocumento: (
    documentoId: string,
    nombreCustom: string,
    tipo: TipoDocumento
  ) => Promise<any>;
  onEliminarDocumento: (documentoId: string) => Promise<any>;
  onDescargarDocumento: (documentoId: string) => void;
  isLoading?: boolean;
  canUpload?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  canDownload?: boolean;
  canSendWhatsApp?: boolean;
}

export const SeccionDocumentacion: React.FC<SeccionDocumentacionProps> = ({
  tramiteId,
  tramite,
  documentos,
  onCargarDocumento,
  onActualizarDocumento,
  onEliminarDocumento,
  onDescargarDocumento,
  isLoading = false,
  canUpload = true,
  canEdit = true,
  canDelete = true,
  canDownload = true,
  canSendWhatsApp = false,
}) => {
  const { openViewer } = usePDFViewer();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [documentoEditando, setDocumentoEditando] = useState<Documento | null>(
    null
  );
  const [documentoAEliminar, setDocumentoAEliminar] =
    useState<Documento | null>(null);
  const [documentoWhatsApp, setDocumentoWhatsApp] = useState<Documento | null>(null);

  const formatearTamano = (bytes?: number): string => {
    if (!bytes) return '';
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  };

  const formatearFecha = (fecha: string): string => {
    try {
      return new Date(fecha).toLocaleDateString('es-AR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return fecha;
    }
  };

  const isPDF = (documento: Documento): boolean => {
    // Check mimeType first, fallback to file extension
    if (documento.mimeType) {
      return documento.mimeType === 'application/pdf';
    }
    return documento.nombre.toLowerCase().endsWith('.pdf');
  };

  const handlePreviewClick = async (documento: Documento) => {
    try {
      const blob = await presupuestoService.descargarDocumento(
        tramiteId,
        documento.id
      );
      const url = URL.createObjectURL(blob);
      openViewer(url, documento.nombreCustom);
    } catch (error) {
      console.error('Error al previsualizar documento:', error);
    }
  };

  const handleAgregarClick = () => {
    setDocumentoEditando(null);
    setDialogOpen(true);
  };

  const handleEditarClick = (documento: Documento) => {
    setDocumentoEditando(documento);
    setDialogOpen(true);
  };

  const handleConfirmDialog = async (data: {
    file?: File;
    nombreCustom: string;
    tipo: TipoDocumento;
  }) => {
    if (documentoEditando) {
      // Modo editar
      await onActualizarDocumento(
        documentoEditando.id,
        data.nombreCustom,
        data.tipo
      );
    } else {
      // Modo agregar
      if (data.file) {
        await onCargarDocumento(data.tipo, data.nombreCustom, data.file);
      }
    }
  };

  const handleEliminarClick = (documento: Documento) => {
    setDocumentoAEliminar(documento);
  };

  const handleConfirmEliminar = async () => {
    if (documentoAEliminar) {
      await onEliminarDocumento(documentoAEliminar.id);
      setDocumentoAEliminar(null);
    }
  };

  return (
    <div className="bg-white border-t mt-4 p-4 rounded-lg shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className="text-lg font-semibold">Documentación</h3>
        {canUpload && (
          <Button onClick={handleAgregarClick} size="sm" disabled={isLoading}>
            <Plus className="w-4 h-4 mr-2" />
            Agregar Documento
          </Button>
        )}
      </div>

      {documentos.length === 0 ? (
        <div className="flex items-center justify-center p-8 bg-muted/30 border border-dashed rounded-lg">
          <div className="text-center">
            <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              No hay documentos cargados
            </p>
            {canUpload && (
              <p className="text-xs text-muted-foreground mt-1">
                Haz clic en "Agregar Documento" para comenzar
              </p>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {documentos.map((documento) => (
            <Card key={documento.id}>
              <CardContent className="p-3 sm:p-4">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 sm:flex-nowrap">
                  <FileText className="w-10 h-10 text-primary flex-shrink-0" />

                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-base truncate">
                      {documento.nombreCustom}
                    </h4>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground flex-wrap">
                      <Badge
                        variant={
                          documento.tipo === 'proforma' ? 'default' : 'secondary'
                        }
                      >
                        {documento.tipo === 'proforma' ? 'Proforma' : 'Factura'}
                      </Badge>
                      <span className="truncate max-w-full sm:max-w-[200px]">
                        {documento.nombre}
                      </span>
                      {documento.size && (
                        <>
                          <span>•</span>
                          <span>{formatearTamano(documento.size)}</span>
                        </>
                      )}
                      <span>•</span>
                      <span>{formatearFecha(documento.fechaCarga)}</span>
                    </div>
                  </div>

                  <div className="flex w-full items-center justify-end gap-1 flex-shrink-0 sm:w-auto">
                    {canEdit && (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleEditarClick(documento)}
                        disabled={isLoading}
                        title="Editar"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                    )}
                    {canDownload && isPDF(documento) && (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handlePreviewClick(documento)}
                        disabled={isLoading}
                        title="Previsualizar"
                        className="hidden md:flex"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    )}
                    {canDownload && (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => onDescargarDocumento(documento.id)}
                        disabled={isLoading}
                        title="Descargar"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    )}
                    {canSendWhatsApp && (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => setDocumentoWhatsApp(documento)}
                        disabled={isLoading}
                        title="Enviar por WhatsApp"
                        className="text-green-600 hover:text-green-700 hover:bg-green-100"
                      >
                        <MessageCircle className="h-4 w-4" />
                      </Button>
                    )}
                    {canDelete && (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleEliminarClick(documento)}
                        disabled={isLoading}
                        title="Eliminar"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog para agregar/editar documento */}
      <DocumentoDialog
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onConfirm={handleConfirmDialog}
        documentoEditar={documentoEditando}
        isLoading={isLoading}
      />

      {/* Dialog de confirmación para eliminar */}
      <Dialog
        open={!!documentoAEliminar}
        onOpenChange={() => setDocumentoAEliminar(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>¿Eliminar documento?</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              ¿Está seguro de eliminar el documento{' '}
              <strong className="text-foreground">
                {documentoAEliminar?.nombreCustom}
              </strong>
              ?
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              Esta acción no se puede deshacer.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDocumentoAEliminar(null)}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmEliminar}
              disabled={isLoading}
            >
              {isLoading ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {canSendWhatsApp && documentoWhatsApp && (
        <WhatsAppContabilidadDocumentoModal
          open={!!documentoWhatsApp}
          onOpenChange={(open) => {
            if (!open) {
              setDocumentoWhatsApp(null);
            }
          }}
          tramite={tramite}
          documento={documentoWhatsApp}
        />
      )}
    </div>
  );
};
